import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { Empty, EmptyDescription, EmptyTitle } from '@Components/ui-v2/empty';
import { FieldServiceWorker, FieldServiceWorkerStatus } from '@Models';
import {
  assignFieldServiceColors,
  FIELD_SERVICE_AVATAR_TEXT_COLOR,
  getFieldServiceInitials,
} from '../assignFieldServiceColor';
import { UPPERCASE_LABEL_CLASS_NAME } from '../constants';

interface WorkersSectionProps {
  workers: FieldServiceWorker[];
}

const STATUS_DOT: Record<FieldServiceWorkerStatus, string> = {
  on: 'bg-success-500',
  off: 'bg-gray-400',
  idle: 'bg-warning-500',
};

const WorkersSection = ({ workers }: WorkersSectionProps) => {
  const { t } = useTranslation();

  const colorsById = useMemo(() => assignFieldServiceColors(workers.map((worker) => worker.id)), [workers]);

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-6">
      <div>
        <span className={cn('block', UPPERCASE_LABEL_CLASS_NAME)}>
          {`${t('FieldService.Sections.Workers')} · ${workers.length}`}
        </span>
        <p className="mb-0 mt-2 text-xs text-gray-500">{t('FieldService.Workers.Description')}</p>
      </div>

      {workers.length === 0 ? (
        <Empty>
          <EmptyTitle>{t('FieldService.Workers.EmptyTitle')}</EmptyTitle>
          <EmptyDescription>{t('FieldService.Workers.EmptyDescription')}</EmptyDescription>
        </Empty>
      ) : (
        <div className="flex flex-col gap-2">
          {workers.map((worker) => {
            const isKnownStatus = worker.status in STATUS_DOT;
            const statusLabel = isKnownStatus ? t(`FieldService.WorkerStatus.${worker.status}`) : worker.status;

            return (
              <div key={worker.id} className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2.5">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold"
                  style={{ backgroundColor: colorsById[worker.id], color: FIELD_SERVICE_AVATAR_TEXT_COLOR }}>
                  {getFieldServiceInitials(worker.name)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-gray-900">{worker.name}</div>
                  {worker.territory_name && (
                    <div className="truncate text-xs text-gray-500">{worker.territory_name}</div>
                  )}
                </div>
                <span className="flex flex-shrink-0 items-center gap-1.5 text-xs font-semibold text-gray-500">
                  <span className={cn('h-1.5 w-1.5 rounded-full', STATUS_DOT[worker.status] ?? 'bg-gray-400')} />
                  {statusLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkersSection;
